import { IPage } from '@types'
import CategoryView from 'components/categories/CategoryView'
import Layout from 'components/layouts/Layout'
import { BASE_URL } from 'config/constants'
import fetcher from 'lib/fetcher'
import { getAllProducts } from 'lib/getAllProducts'
import { getMenus } from 'lib/getMenus'
import { buildCategoryGraph } from 'lib/jsonLd'
import { setCacheControl } from 'lib/misc'
import { getCanonicalCategory } from 'lib/productCanonical'
import { isAbsent, isEmptyArray, isNonEmptyString, isSame } from 'lib/utils'
import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next'
import Head from 'next/head' 

import { ICategory, IMenuItem } from 'store/api'

export let getServerSideProps = async ({
  req,
  params,
  res,
}: GetServerSidePropsContext) => {
  // Stop from running on client
  let isFirstServerCall = isSame(req?.url?.indexOf('/_next/data/'), -1)

  const emptyProps = {
    title: '',
    categoryListSSR: [] as ICategory[],
    productListSSR: [],
    menus: {} as Record<string, IMenuItem[]>,
    jsonLd: '',
  }

  if (!isFirstServerCall) return { props: emptyProps }

  let slug = params?.['slug'] as string[] | undefined
  if (isEmptyArray(slug)) return { props: emptyProps, notFound: true }

  let { data: categoryData } = await fetcher<{ categories: ICategory[] }>({
    url: `${BASE_URL}/v1/categories/getall`,
    headers: { lng: 'ru' },
  })

  let categoryMain = categoryData?.categories.find((x) =>
    isSame(x.slug, slug![0])
  )
  let categorySub =
    slug!.length === 2
      ? categoryMain?.sub.find((c) => isSame(c.slug, slug![1]))
      : undefined

  if (isNonEmptyString(slug![1]) && isAbsent(categorySub))
    return { props: emptyProps, notFound: true }

  if (isAbsent(categoryMain?.id))
    return { props: emptyProps, notFound: true }

  setCacheControl(res)

  let { products } = await getAllProducts('ru')
  let menus = await getMenus('ru')

  // Собираем JSON-LD
  const productList =
    categorySub === undefined
      ? products[categoryMain!.id] ?? []
      : products[categorySub.id] ?? []

  const jsonLd = JSON.stringify(
    buildCategoryGraph({
      category: categorySub ?? categoryMain!,
      parentCategory: categorySub ? categoryMain : undefined,
      products: productList,
      // url/@id товаров — на их canonical (то же правило, что у
      // страницы товара), чтобы @id одного товара не расходились.
      canonicalCategoryOf: (p) =>
        getCanonicalCategory(categoryData?.categories ?? [], products, p),
    })
  )

  if (categorySub === undefined)
    return {
      props: {
        title: categoryMain!.name,
        categoryListSSR: categoryData?.categories ?? [],
        productListSSR: productList,
        menus,
        jsonLd,
      },
    }
  else
    return {
      props: {
        title: categoryMain!.name + ' | ' + categorySub.name,
        categoryListSSR: categoryData?.categories ?? [],
        productListSSR: productList,
        menus,
        jsonLd,
      },
    }
}

export type CategoryPageProps = Required<
  InferGetServerSidePropsType<typeof getServerSideProps>
>

let CategoryPage: IPage<CategoryPageProps> = (props) => (
  <>
    <Head>
      {props.jsonLd && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: props.jsonLd }}
        />
      )}
    </Head>
    <CategoryView {...props} />
  </>
)

CategoryPage.getLayout = (page: { props: CategoryPageProps }) => (
  <Layout
    title={page.props?.title}
    // description={page.props?.description}
    // keywords={page.props?.keywords}
    index
    menus={page.props.menus}
    withCartPanel
  >
    {page}
  </Layout>
)

export default CategoryPage
